import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

export function useStoryByCode(code: string | undefined) {
  return useQuery({
    queryKey: ['story', 'code', code],
    queryFn: () => api.getStoryByCode(code!),
    enabled: !!code,
  });
}

export function useStoryDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['story', id],
    queryFn: () => api.getStoryDetail(id!),
    enabled: !!id,
  });
}

export function useJoinStory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code: string) => api.joinStory(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['story'] });
    },
  });
}

export function useAddEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ storyId, text }: { storyId: string; text: string }) =>
      api.addEntry(storyId, text),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['story', variables.storyId] });
    },
  });
}

// ============================================
// Anonymous hooks
// ============================================

export function useJoinStoryAnonymous() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      code,
      anonymousId,
      displayName,
    }: {
      code: string;
      anonymousId: string;
      displayName: string;
    }) => api.joinStoryAnonymous(code, anonymousId, displayName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['story'] });
    },
  });
}

export function useStoryDetailAnonymous(
  id: string | undefined,
  anonymousId: string | undefined
) {
  return useQuery({
    queryKey: ['story', id, 'anonymous', anonymousId],
    queryFn: () => api.getStoryDetailAnonymous(id!, anonymousId!),
    enabled: !!id && !!anonymousId,
  });
}

export function useAddEntryAnonymous() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      storyId,
      anonymousId,
      text,
    }: {
      storyId: string;
      anonymousId: string;
      text: string;
    }) => api.addEntryAnonymous(storyId, anonymousId, text),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['story', variables.storyId] });
    },
  });
}

// Public completed story (no auth required)
export function useCompletedStory(id: string | undefined) {
  return useQuery({
    queryKey: ['story', id, 'completed'],
    queryFn: () => api.getCompletedStory(id!),
    enabled: !!id,
  });
}
